// `/meus-quadrinhos/:id/publicar` — AS DUAS CHAVINHAS.
//
// T2.56, e a fala já traz as DUAS: *"você poderia colocar uma chavinha, né, se tá terminado ou
// não, se é para disponibilizar público ou não."* Dito aos 13:41, dois minutos antes de ele dar
// nome aos dois eixos no T2.61.
//
// ── POR QUE ESTA TELA EXISTE, E NÃO É "MAIS UMA TELA" ───────────────────────────────────────
//
// Medido em 2026-09-12: **nada no produto escrevia na coleção `biblioteca`.** A leitura estava
// pronta e provada — consulta, regras, cartões, séries, categorias — e o ato de publicar não
// existia. Então o `T2.53` (*"se eu coloquei como público os meus quadrinhos, eles deveriam
// aparecer aqui"*) descrevia uma ação impossível de fazer.
//
// Esta é a tela que torna a frase dele verdadeira.
//
// ── A ORDEM DAS DUAS PERGUNTAS É DELIBERADA ─────────────────────────────────────────────────
//
// Primeiro "quem pode ver", depois "está encerrado". Não é a ordem em que ele falou — ele disse
// "terminado" antes —, e a razão de inverter é o que cada uma custa desfazer: despublicar é um
// clique, e finalizar **não tem volta** (dúvida D2, decidida: a saída de quem se arrependeu é
// deletar e subir outro). A decisão irreversível vem por último, com o aviso ao lado dela.
//
// ── O QUE ESTA TELA NÃO FAZ ─────────────────────────────────────────────────────────────────
//
// Não autoriza. `firestore.rules` decide, e 20 casos novos do `prova-regras/` provam contra o
// motor de produção: finalizado tranca até o autor, despublicar um finalizado continua passando
// (os eixos são independentes), e desfinalizar não existe.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import GuardaDoAcervo from "../pages/meus-quadrinhos/guarda";
import Nav from "../components/nav";
import AppUser from "../api/app-user.class";
import Firebase from "../api/firebase.class";
import { souContaDeValidacao } from "../validacao/a-conta-de-validacao";
import { motivoDeRecusaPorVazio } from "./tem-conteudo";
import {
  Categoria,
  ROTULO_CATEGORIA,
  ROTULO_RETICULA,
  RETICULAS,
  Reticula,
} from "../api/documento.types";
import { fichaPublicavel, paginasPublicaveis } from "./converter";
import { capaDaFicha, primeiroDesenho } from "./capa-da-ficha";
import { mensagemDaFalha } from "./mensagem-da-falha";
import {
  EscolhaDePublicacao,
  pesoDaPagina,
  publicacaoDe,
} from "./publicacao";
import {
  motivoDeRecusaDepoisDeSeparar,
  separarTudo,
} from "./imagens-em-documentos";
import { planoDePublicacao } from "./plano-de-publicacao";
import { meusVinculos } from "../instituicao/api";
import { ESPERA_DO_ACERVO_MS, resolverQuadrinho } from "../pages/meus-quadrinhos/quadrinho-do-acervo";
import { conferirTextos, ConferenciaIndisponivelError } from "../api/moderar-texto";
import { textosDasPaginas } from "./textos-do-quadrinho";
import { Vinculo } from "../instituicao/modelo";

import "./publicar-quadrinho.scss";

function Formulario({ appUser }: { appUser: AppUser }) {
  const { id } = useParams();
  const navigate = useNavigate();

  // ── T8.4, A TERCEIRA VEZ, E ESTA FOI MEDIDA CONTRA O FIRESTORE LOCAL ─────────────────────
  //
  // Em 27/09/2026, com uma conta criada pela tela de cadastro e um quadrinho salvo com sete imagens:
  // `/meus-quadrinhos/{id}/editar` abria com tudo no palco, e `/meus-quadrinhos/{id}/publicar`
  // respondia "Não encontrei esse quadrinho" — em 3 s, em 8 s e em 16 s.
  //
  // A causa eram as duas linhas que este `useMemo` tinha: `[appUser, id]`. O acervo chega por
  // snapshot DEPOIS da montagem, e `appUser` é o MESMO objeto para sempre, mutado por dentro — então
  // a dependência nunca dispara e a lista vazia da montagem responde para sempre. É a mesma causa
  // que o `T8.4` já custou no editor, e a razão inteira está em `quadrinho-do-acervo.ts`.
  //
  // Enquanto o acervo pode chegar, a tela DIZ que está abrindo. Ausência não é estado.
  const comics = appUser.Comics();
  const [esgotou, setEsgotou] = useState(false);
  useEffect(() => {
    setEsgotou(false);
    const conta = setTimeout(() => setEsgotou(true), ESPERA_DO_ACERVO_MS);
    return () => clearTimeout(conta);
  }, [id, comics]);
  const achado = resolverQuadrinho(comics, id, null, esgotou);
  const comic = achado.estado === "achei" ? achado.comic : undefined;

  // ── T8.13 · O PADRÃO DE VISIBILIDADE, E ELE NASCIA LIGADO ──────────────────────────────
  //
  // "Numa plataforma usada por crianças, a opção de tornar público já vem marcada."
  //
  // Medido em produção em 24/09/2026: a caixa "Deixar público na biblioteca" abria com
  // `checked: true`. Quem não lesse a tela publicava para a plataforma inteira sem ter
  // escolhido isso — e o custo do engano é assimétrico. Publicar sem querer expõe trabalho de
  // criança; não publicar sem querer custa um clique depois.
  //
  // O padrão passa a ser o estado reversível. A sugestão T8.36 pede a mesma coisa, com outras
  // palavras, e as duas se atendem aqui.
  const [publicado, setPublicado] = useState(false);
  const [finalizado, setFinalizado] = useState(false);
  // ── A CAPA NA FICHA — `T8.17`, saida (A), escolhida por ele em 28/09 ──────────────────
  //
  // NASCE DESMARCADA de proposito. A capa atravessa a linha que separa a ficha (publica) das
  // paginas (exigem conta): com ela, um pedaco do desenho da crianca passa a ser legivel sem
  // conta. Consentimento que vem marcado nao e consentimento — e aqui quem consente e uma
  // crianca sobre o proprio desenho.
  const [comCapa, setComCapa] = useState(false);
  const [categoria, setCategoria] = useState<Categoria>("livre");
  const [escola, setEscola] = useState("");
  /**
   * Esta conta e de validacao? Lido UMA vez, aqui, e nao dentro de `publicacaoDe` — aquela
   * funcao e pura e precisa continuar sendo. O padrao inteiro esta em
   * `src/validacao/a-conta-de-validacao.ts`.
   *
   * Comeca `false`, que e a direcao segura NESTE sentido: enquanto a leitura nao voltar, a tela
   * trata a conta como comum. O oposto — supor validacao — esconderia da biblioteca o quadrinho
   * de uma crianca de verdade se a leitura demorasse.
   */
  const [ehDeValidacao, setEhDeValidacao] = useState(false);
  const [reticula, setReticula] = useState<Reticula>("nenhuma");
  const [vinculos, setVinculos] = useState<Vinculo[]>([]);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // A marca da conta de validacao, lida uma vez ao abrir a tela. Qualquer erro vira `false`:
  // conter por engano um quadrinho de crianca e pior do que publicar um de teste, e o de teste
  // ainda e pego pela regra do banco, que recusa `publicado` vindo de conta marcada.
  useEffect(() => {
    let vivo = true;
    souContaDeValidacao()
      .then((sim) => { if (vivo) setEhDeValidacao(sim); })
      .catch(() => { if (vivo) setEhDeValidacao(false); });
    return () => { vivo = false; };
  }, []);

  useEffect(() => {
    document.title = "Publicar · HQ-Lab";
    return () => {
      document.title = "HQ-Lab";
    };
  }, []);

  useEffect(() => {
    // T2.63 — só dá para publicar NA escola onde a pessoa é educador ATIVO. Aluno ativo LÊ o
    // material da instituição; quem o põe lá é o educador, pela mesma razão que ele modera: é a
    // conta que a escola respondeu por.
    meusVinculos(appUser.Id()).then((vs) =>
      setVinculos(vs.filter((v) => v.situacao === "ativo" && v.papel === "educador"))
    );
  }, [appUser]);

  // O peso de cada página, calculado ANTES de enviar. É o que permite recusar com explicação em
  // vez de o Firestore recusar no meio da operação — e deixar a ficha publicada apontando para
  // páginas que não subiram.
  const paginas = useMemo(
    () => (comic ? paginasPublicaveis(comic, appUser, reticula) : []),
    [comic, appUser, reticula]
  );
  // ── AS IMAGENS SAEM DA PÁGINA ANTES DE ELA SER PESADA (T6.6) ──────────────────────────────
  //
  // Ele, na letra: *"Ficou 1.20 MB. Já não pode publicar porque tem 1.20 MB. (...) No mínimo 5 MB.
  // E nas páginas que tiverem 5 ou 6 imagens acabou, nunca vai postar."*
  //
  // O número dele está certo como OBJETIVO e errado como mecanismo: medido em 17/09 contra o
  // emulador, o Firestore recusa qualquer documento acima de 1.048.487 bytes, então subir o nosso
  // teto para 5 MB só moveria a recusa para o meio da gravação — com a ficha já escrita, que é o
  // defeito do T4.13. Ver `imagens-em-documentos.ts` para a medição inteira e para o Storage, que
  // seria o conserto de verdade e está fora do plano de cobrança do projeto.
  const separadas = useMemo(() => separarTudo(paginas), [paginas]);
  const recusaPorTamanho = useMemo(() => motivoDeRecusaDepoisDeSeparar(separadas), [separadas]);

  /**
   * T8.13 · PUBLICAR UM QUADRINHO EM BRANCO.
   *
   * Ele: *"A tela também aceita publicar um quadrinho sem nenhum conteúdo (o teste tinha só quadros em
   * branco, '1 KB')."* Medido em 27/09: não havia verificação nenhuma — uma folha com seis molduras
   * vazias ia para a biblioteca aberta, com o nome da criança e o da escola.
   *
   * A grade de quadros NASCE com a página, então moldura não é conteúdo. O porquê da régua está em
   * `tem-conteudo.ts`.
   */
  const recusaPorVazio = useMemo(() => motivoDeRecusaPorVazio(paginas), [paginas]);
  /** As duas recusas, na ordem em que fazem sentido ler: em branco vem antes de grande demais. */
  const recusa = recusaPorVazio ?? recusaPorTamanho;

  if (achado.estado === "esperando") {
    return (
      <div className="pub-container">
        <Nav />
        <main className="pub-nao-achei">
          <h1>Abrindo o seu quadrinho…</h1>
        </main>
      </div>
    );
  }

  if (!comic) {
    return (
      <div className="pub-container">
        <Nav />
        <main className="pub-nao-achei">
          <h1>Não encontrei esse quadrinho</h1>
          <button type="button" onClick={() => navigate("/meus-quadrinhos")}>
            Voltar para meus quadrinhos
          </button>
        </main>
      </div>
    );
  }

  // A GUARDA ACIMA JA PROVOU QUE `comic` EXISTE, e o TypeScript nao acredita — porque
  // `async function` e HOISTADA: para o compilador, ela poderia ser chamada antes do `return` de
  // cima. Capturar o valor estreitado numa constante e o jeito de dizer isso sem `!`, que
  // silenciaria a checagem em vez de satisfaze-la.
  const oQuadrinho = comic;
  const titulo = oQuadrinho.Name() || "Quadrinho sem nome";

  async function publicar() {
    if (ocupado) return;
    if (recusa) {
      setErro(recusa);
      return;
    }
    setOcupado(true);
    setErro(null);

    // Sai do `try` de propósito: o `catch` precisa dela, e dentro do `try` ela não existiria
    // quando a falha acontecesse ANTES da leitura da ficha.
    let finalizadoNoServidor = false;

    const escolha: EscolhaDePublicacao = { publicado, finalizado, categoria };
    const publicacao = publicacaoDe({ ...escolha, deValidacao: ehDeValidacao }, escola || undefined);

    try {
      // ── A BARREIRA DE TEXTO, ANTES DE QUALQUER ESCRITA (BL-2.5) ────────────────────────────
      //
      // É AQUI e não no editor: no editor a criança está pensando, e uma barreira que interrompe
      // a cada letra ensina que a plataforma vigia o rascunho dela. No publicar a pergunta é
      // outra — "isto pode ser lido pela escola inteira?" —, e essa é legítima.
      //
      // Vem ANTES da ficha porque publicar é uma escrita seguida de N escritas: recusar no meio
      // deixaria o quadrinho meio publicado, com ficha na biblioteca e páginas faltando.
      const textos = textosDasPaginas(titulo, paginas);
      try {
        const veredicto = await conferirTextos(textos.map((t) => t.texto));
        if (!veredicto.permitido) {
          // O ENDEREÇO JUNTO COM A RECUSA. Dizer "tem palavra proibida no seu quadrinho" e não
          // dizer onde obriga a criança a caçar num quadrinho de dez páginas.
          const lista = veredicto.recusados
            .map((r) => `• ${textos[r.indice]?.onde || "em algum lugar"}: ${r.mensagem}`)
            .join("\n");
          setErro(
            `Este quadrinho ainda não pode ser publicado:\n${lista}\n\n` +
              "Corrija no editor e publique de novo. Nada do que você fez se perdeu."
          );
          setOcupado(false);
          return;
        }
      } catch (e) {
        // FALHA FECHADA, e é o oposto do que a tradução de cena faz. Lá, falhar devolve o
        // português e a criança recebe o desenho. Aqui, deixar passar publica ofensa na
        // biblioteca da escola com o nome de uma criança embaixo — irreversível de um lado,
        // um clique do outro.
        if (e instanceof ConferenciaIndisponivelError) {
          console.warn("[publicar] conferência de texto indisponível:", e.message);
          setErro(
            "Não consegui conferir os textos do quadrinho agora, então não vou publicar sem " +
              "isso. Não é problema com o que você escreveu — tente de novo em instantes. Seu " +
              "quadrinho continua salvo em Meus quadrinhos."
          );
          setOcupado(false);
          return;
        }
        throw e;
      }

      // ══ PUBLICAR EM DUAS FASES, E É O T4.13 ═════════════════════════════════════════════
      //
      // Ele, na letra: *"Eu tento publicar isso aqui / Vou colocar como pronta / Não deu publicar
      // agora / (...) Na real, ela disse que conseguiu, mas não conseguiu de verdade / Tentei
      // acessar e não foi."*
      //
      // PUBLICAR É UMA ESCRITA SEGUIDA DE N ESCRITAS, e não havia volta atrás. A ficha ia
      // primeiro — a ordem é obrigatória, porque a regra que autoriza escrever uma página
      // consulta a ficha (`fichaDe(docId)`) e sem ela nega. Se uma página falhasse no meio, a
      // ficha JÁ ESTAVA na biblioteca com `estado: "publicado"`:
      //
      //   · o quadrinho aparecia como publicado em "Meus quadrinhos" (o estado é lido de
      //     `biblioteca/{id}`, e a ficha estava lá);
      //   · a tela mostrava "Não deu para publicar agora";
      //   · e abrir o item na biblioteca não achava as páginas.
      //
      // É exatamente "ela disse que conseguiu, mas não conseguiu de verdade". A ficha é o
      // anúncio; as páginas são a história. O anúncio subia sozinho.
      //
      // ── POR QUE DUAS FASES E NÃO UMA TRANSAÇÃO ──────────────────────────────────────────
      //
      // `writeBatch` não resolve: as regras do Firestore avaliam cada escrita do lote contra o
      // estado ANTERIOR ao lote, então a página continuaria sendo negada por falta de ficha.
      //
      // O que resolve é a ficha nascer INVISÍVEL. A regra de leitura da biblioteca é
      // `estado == 'publicado'`, e a de escrita de página só exige autoria e não-finalizado —
      // logo uma ficha em `rascunho` autoriza as páginas e não aparece para ninguém. Só depois
      // que todas as páginas subiram é que a ficha vira o que a pessoa escolheu.
      //
      // Falhou no meio? Fica uma ficha `rascunho` sem anúncio nenhum: a biblioteca não a mostra,
      // "Meus quadrinhos" a lê como guardada, e a mensagem de erro passa a ser verdadeira.
      const id = oQuadrinho.getId();

      // A CAPA SE GERA ANTES DE QUALQUER ESCRITA, e por dois motivos: ela pode falhar (imagem que
      // nao decodifica, capa que nao cabe no teto), e falhar DEPOIS da fase 1 deixaria a ficha
      // invisivel gravada por causa de uma miniatura. Falhar aqui so significa publicar sem capa,
      // que e o comportamento de sempre.
      let capa: string | null = null;
      if (comCapa) {
        const gerada = await capaDaFicha(primeiroDesenho(paginas[0])).catch(() => null);
        capa = gerada?.imagem ?? null;
        if (!capa) {
          console.info("[publicar] sem capa: nenhum desenho serviu, ou nao coube no teto");
        }
      }

      const ficha = (p: typeof publicacao) =>
        fichaPublicavel({
          comic: oQuadrinho,
          appUser,
          titulo,
          publicacao: p,
          quantasPaginas: paginas.length,
          capa,
        });

      // FASE 1 — a ficha invisível, e SÓ quando ela ainda não existe.
      //
      // Se o quadrinho já está na biblioteca, reescrevê-lo como `rascunho`/`não finalizado` seria
      // uma mudança de estado por conta própria — e num item já finalizado a regra recusaria, o
      // que quebraria o despublicar que o T2.61 garante. Com a ficha já lá, as páginas já estão
      // autorizadas e a fase 1 não tem o que fazer.
      // A SEQUÊNCIA VEM DE `planoDePublicacao`, E NÃO DAQUI, porque a propriedade que importa é
      // uma propriedade da ORDEM — e ordem dentro de um `async` com Firebase só se verifica com o
      // Firebase de pé. Como dado, ela se verifica com `expect`, e é o que
      // `plano-de-publicacao.test.ts` faz em nove casos.
      // A FICHA QUE JÁ ESTÁ LÁ, e não só o "existe". A mensagem de erro precisava saber se o
      // quadrinho está finalizado no SERVIDOR, e este `get` já era feito — só jogava fora o
      // documento. Saber é de graça; supor custou o achado #02 do relatório de QA.
      const fichaNoServidor = (await Firebase.getDocument("biblioteca", id)) as
        | { publicacao?: { finalizado?: boolean } }
        | null;
      finalizadoNoServidor = fichaNoServidor?.publicacao?.finalizado === true;
      const jaEstaNaBiblioteca = !!fichaNoServidor;
      for (const passo of planoDePublicacao(paginas.length, publicacao, jaEstaNaBiblioteca)) {
        if (passo.tipo === "ficha") {
          await Firebase.setDocument("biblioteca", id, ficha(passo.publicacao));
        } else {
          // A IMAGEM VAI ANTES DA PÁGINA QUE A REFERENCIA, e a ordem é a decisão.
          //
          // Falhando no meio, sobra imagem sem página — invisível e inofensiva. Na ordem inversa
          // sobraria página apontando para imagem que não subiu, e a criança abriria o quadrinho
          // com quadros vazios. É a mesma lógica do `anuncioEhOUltimoPasso`: quando não dá para
          // ser atômico, escolhe-se qual lixo a falha deixa.
          const { pagina, imagens } = separadas[passo.numero - 1];
          for (const img of imagens) {
            await Firebase.setDocument(
              `biblioteca/${id}/paginas`,
              img.id,
              img as unknown as Record<string, unknown>
            );
          }
          await Firebase.setDocument(
            `biblioteca/${id}/paginas`,
            String(passo.numero),
            pagina as unknown as Record<string, unknown>
          );
        }
      }

      navigate("/meus-quadrinhos", {
        state: { publicou: titulo, publicado, finalizado },
      });
    } catch (e) {
      console.error("[publicar] falhou", e);
      setErro(mensagemDaFalha(e, finalizadoNoServidor));
      setOcupado(false);
    }
  }

  return (
    <div className="pub-container">
      <Nav />

      <main className="pub-cartao">
        <h1>Publicar “{titulo}”</h1>
        <p className="pub-resumo">
          {paginas.length === 1 ? "1 página" : `${paginas.length} páginas`} ·{" "}
          {(paginas.reduce((t, p) => t + pesoDaPagina(p), 0) / 1024).toFixed(0)} KB
        </p>

        {erro && (
          <p className="pub-erro" role="alert">
            {erro}
          </p>
        )}
        {recusa && !erro && (
          <p className="pub-erro" role="alert">
            {recusa}
          </p>
        )}

        {/* ── CHAVINHA 1: QUEM PODE VER ────────────────────────────────────────────────── */}
        <section className="pub-eixo">
          <label className="pub-chave">
            <input
              type="checkbox"
              checked={publicado}
              disabled={ocupado}
              onChange={(e) => setPublicado(e.target.checked)}
            />
            <span>
              <strong>Deixar público na biblioteca</strong>
              {/* A AJUDA DIZ O QUE O CLIQUE FAZ, e não onde você já está. Com a caixa
                  desmarcada, a frase antiga ("Fica guardado só para você") descrevia o estado
                  atual — e ao lado de um rótulo no imperativo lê-se como se fosse o efeito de
                  marcar. É o mesmo defeito que o achado #13 aponta no "Marcar como pronto". */}
              <small>
                {publicado
                  ? "Marcado: outras pessoas da plataforma vão poder ler esta história."
                  : "Desmarcado, fica guardado só para você. Marque para deixar na biblioteca aberta."}
              </small>
            </span>
          </label>

          {publicado && (
            <>
              <label className="pub-campo">
                <span>Prateleira</span>
                <select
                  value={categoria}
                  disabled={ocupado}
                  onChange={(e) => setCategoria(e.target.value as Categoria)}
                >
                  {(Object.keys(ROTULO_CATEGORIA) as Categoria[]).map((c) => (
                    <option key={c} value={c}>
                      {ROTULO_CATEGORIA[c]}
                    </option>
                  ))}
                </select>
              </label>

              {/* T2.63 — só aparece para quem é educador ativo em alguma escola. */}
              {vinculos.length > 0 && (
                <label className="pub-campo">
                  <span>Só para a minha escola?</span>
                  <select
                    value={escola}
                    disabled={ocupado}
                    onChange={(e) => setEscola(e.target.value)}
                  >
                    <option value="">Não — fica na biblioteca aberta</option>
                    {vinculos.map((v) => (
                      <option key={v.instituicaoId} value={v.instituicaoId}>
                        Só para {v.instituicaoId}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </>
          )}
        </section>

        {/* ── A RETÍCULA — T2.108, que ele chamou de "essencial" ────────────────────────
            "tem que ter uma forma de aplicar retícula em TODAS AS PÁGINAS, se quiser, para tentar
             criar uma UNIFORMIDADE dos desenhos, do traço."

            A escolha é UMA e vale para o quadrinho inteiro, porque é disso que a uniformidade
            depende — uma retícula por página seria o oposto do que ele pede.

            E ela mora aqui, na publicação, e não no editor: é no momento de publicar que a
            página vira coisa que outra turma lê, e "uniformidade" é sobre o que se lê. A metade
            que falta — aplicar por QUADRO, dentro do editor ("em qualquer um dos campos") — está
            dita no nó do épico, com a razão. */}
        <section className="pub-eixo">
          <label className="pub-campo pub-campo-largo">
            <span>Retícula (a trama de pontinhos da marca)</span>
            <select
              value={reticula}
              disabled={ocupado}
              onChange={(e) => setReticula(e.target.value as Reticula)}
            >
              {RETICULAS.map((r) => (
                <option key={r} value={r}>
                  {ROTULO_RETICULA[r]}
                </option>
              ))}
            </select>
          </label>
          <p className="pub-ajuda">
            Ela passa por cima de todas as páginas e ajuda a deixar os desenhos com a mesma cara —
            útil quando os quadros saíram com traços diferentes.
          </p>
        </section>

        {/* ── CHAVINHA: A CAPA NA BIBLIOTECA — `T8.17` ─────────────────────────────────── */}
        <section className="pub-eixo">
          <label className="pub-chave">
            <input
              type="checkbox"
              checked={comCapa}
              disabled={ocupado}
              onChange={(e) => setComCapa(e.target.checked)}
            />
            <span>
              <strong>Mostrar o primeiro desenho na biblioteca</strong>
              <small>
                {comCapa
                  ? "Marcado: o primeiro desenho aparece pequeno no cartão, e quem não tem conta vê esse pedaço."
                  : "Desmarcado, o cartão mostra só título, autor e escola — o desenho fica para quem abre a história."}
              </small>
            </span>
          </label>

          {/* O AVISO SÓ APARECE COM A CHAVE LIGADA, e diz o que não é óbvio: as páginas continuam
              exigindo conta, e é só a miniatura que atravessa. Sem essa frase, "aparece na
              biblioteca" soa como a história inteira ficando pública. */}
          {comCapa && (
            <p className="pub-aviso">
              Só a miniatura fica visível sem conta — as páginas continuam exigindo conta para serem
              lidas. Para tirá-la depois, é preciso publicar de novo sem a marca.
            </p>
          )}
        </section>

        {/* ── CHAVINHA 2: ESTÁ ENCERRADO ───────────────────────────────────────────────── */}
        <section className="pub-eixo">
          <label className="pub-chave">
            <input
              type="checkbox"
              checked={finalizado}
              disabled={ocupado}
              onChange={(e) => setFinalizado(e.target.checked)}
            />
            <span>
              <strong>Marcar como pronto</strong>
              {/* T8.13, na letra: "O texto de 'Marcar como pronto' descreve o estado
                  DESMARCADO, o que confunde." Descrevia mesmo — e a correção não é mudar o
                  rótulo, é a ajuda falar do que acontece AO MARCAR. */}
              <small>
                {finalizado
                  ? "Marcado: o trabalho está encerrado, e nem você vai poder editar depois."
                  : "Desmarcado, continua em construção. Marque para encerrar — e encerrar não tem volta."}
              </small>
            </span>
          </label>

          {/* O AVISO APARECE SÓ QUANDO A CHAVE ESTÁ LIGADA, e diz o que não tem volta.
              A dúvida D2 decidiu que a saída existe — deletar e subir outro — e essa saída é
              parte do aviso: sem ela, "não tem volta" soa como perda total, e a pessoa marca
              sem entender ou não marca nunca. */}
          {finalizado && (
            <p className="pub-aviso" role="status">
              <strong>Isto não tem volta.</strong> Depois de marcar como pronto, nem você pode
              editar este quadrinho. Se mudar de ideia, dá para <strong>excluir</strong> e publicar
              outro no lugar.
            </p>
          )}
        </section>

        <div className="pub-acoes">
          <button
            type="button"
            className="pub-confirmar"
            disabled={ocupado || !!recusa}
            onClick={publicar}
          >
            {ocupado ? "Publicando…" : "Publicar"}
          </button>
          <button
            type="button"
            className="pub-cancelar"
            disabled={ocupado}
            onClick={() => navigate("/meus-quadrinhos")}
          >
            Cancelar
          </button>
        </div>
      </main>
    </div>
  );
}

function PublicarQuadrinho() {
  return (
    <GuardaDoAcervo>{({ appUser }) => <Formulario appUser={appUser} />}</GuardaDoAcervo>
  );
}

export default PublicarQuadrinho;
